import React, { useEffect, useMemo, useRef, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import {
    Mic, MicOff, Loader2, X, Wand2, Bold, Italic, Underline as UnderlineIcon,
    List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Redo, Undo, Printer,
    Edit3, Save
} from "lucide-react";
import ApiServices from "../../../../../platform/app/src/shared/ApiServices/ApiServices";
import "../../../../../platform/app/src/routes/voice-dictation/VoiceRecording.css";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import { getQueryParam } from "../../../../../platform/app/src/utils/queryParams";
import { toast } from "react-toastify";
import parse from "html-react-parser";
import MarkdownIt from "markdown-it";
import { saveReport } from "./utils/reportSaveUtils";

import {
    processPunctuation,
    processMedicalText,
    containsPunctuationCommands,
} from "./utils/punctuationUtils";

/** Compact typography preset used by both Preview and the TipTap editor */
const PROSE_COMPACT =
    "prose max-w-none dark:prose-invert " +
    "prose-h1:text-2xl prose-h1:font-bold prose-h1:mb-4 " +
    "prose-h2:text-xl prose-h2:font-semibold prose-h2:mt-4 prose-h2:mb-2 " +
    "prose-p:my-2 prose-li:my-0 " +
    "prose-strong:text-gray-900 dark:prose-strong:text-white " +
    "prose-a:text-blue-600 dark:prose-a:text-blue-400 hover:underline " +
    "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0";

declare global {
    interface Window {
        webkitSpeechRecognition: any;
        SpeechRecognition: any;
    }
}

type PatientReportResponse =
    | {
        report_id?: string;
        patient_id?: string;
        patient_name?: string;
        report_title?: string;
        generated_at?: string;
        report_date?: string;

        // Primary keys we expect
        report_markdown?: string;
        report_html?: string;
        result?: string;

        // Back-compat keys
        report?: string;
        text?: string;
        content?: string;
        markdown?: string;
        html?: string;
        report_content?: string;

        message?: string;
        metadata?: Record<string, any>;
    }
    | string;

type DictationState = { active: boolean; pre: string; post: string; committed: string };

const cx = (...a: (string | false | null | undefined)[]) => a.filter(Boolean).join(" ");
const looksLikeHTML = (s: string) => /<\/?[a-z][\s\S]*>/i.test(s.trim());
const applyPunctuation = (text: string, { finalize = false }: { finalize?: boolean } = {}) => {
    if (!text) return text;
    if (finalize) return processMedicalText(text);
    return containsPunctuationCommands(text) ? processPunctuation(text) : text;
};

/* ---------- Normalize AI Markdown (critical fix) ---------- */
/** Ensures Markdown headings are valid by inserting a space after #… if missing. */
function normalizeMarkdown(mdText: string): string {
    if (!mdText) return mdText;

    let t = mdText;

    // 1) Convert "##Report" -> "## Report" for all heading levels
    t = t.replace(/^(#{1,6})(\S)/gm, (_m, hashes: string, next: string) => `${hashes} ${next}`);

    // 2) Collapse 3+ blank lines to max 2 (optional)
    t = t.replace(/\n{3,}/g, "\n\n");

    // 3) Trim trailing spaces on each line (optional)
    t = t.replace(/[ \t]+$/gm, "");

    return t;
}

/* -------------------- speech hook -------------------- */
function useSpeechRecognition(opts: {
    lang?: string;
    continuous?: boolean;
    interimResults?: boolean;
    onFinal?: (t: string) => void;
    onInterim?: (t: string) => void;
}) {
    const { lang = "en-US", continuous = true, interimResults = true, onFinal, onInterim } = opts;
    const recRef = useRef<any | null>(null);
    const [listening, setListening] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const start = useCallback(() => {
        const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SR) return setError("Speech recognition is not supported in this browser.");
        try {
            const rec = new SR();
            recRef.current = rec;
            rec.lang = lang;
            rec.continuous = continuous;
            rec.interimResults = interimResults;

            rec.onstart = () => { setListening(true); setError(null); };
            rec.onerror = (e: any) => setError(e?.error || "Unknown speech error");
            rec.onend = () => setListening(false);
            rec.onresult = (evt: any) => {
                let interim = "", final = "";
                for (let i = evt.resultIndex; i < evt.results.length; i++) {
                    const t = evt.results[i][0].transcript;
                    if (evt.results[i].isFinal) final += t; else interim += t;
                }
                interim && opts.onInterim?.(interim);
                final && opts.onFinal?.(final);
            };
            rec.start();
        } catch (e: any) {
            setError(e?.message || "Failed to start recognition");
        }
    }, [lang, continuous, interimResults, opts]);

    const stop = useCallback(() => {
        recRef.current?.stop();
        recRef.current = null;
        setListening(false);
    }, []);

    const abort = useCallback(() => {
        recRef.current?.abort();
        recRef.current = null;
        setListening(false);
    }, []);

    return { listening, error, start, stop, abort };
}

/* -------------------- Brand/footer -------------------- */
const BRAND = {
    name: "Symbian-Rad Diagnostics",
    website: "https://rad-demo.symbianhealth.com",
    phone: "+91 00000 00000",
    email: "support@symbian-rad.com",
    // MUST be a public path so the print iframe can load it
    logoUrl: "/assets/symbianhealth_logo.png",
};

/* -------------------- Toolbar button -------------------- */
const ToolbarButton: React.FC<{
    active?: boolean;
    onClick?: () => void;
    title?: string;
    disabled?: boolean;
    children: React.ReactNode;
}> = ({ active, onClick, title, disabled, children }) => (
    <button
        type="button"
        title={title}
        onClick={onClick}
        disabled={disabled}
        className={cx(
            "rounded-xl p-2.5 transition-colors",
            "text-slate-600 dark:text-slate-300",
            "hover:bg-blue-50 dark:hover:bg-blue-900/20",
            active && "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400",
            disabled && "opacity-40 cursor-not-allowed pointer-events-none"
        )}
    >
        {children}
    </button>
);

const ReportCreateDictation = forwardRef<{ handleSaveReport: () => Promise<void> }>((_, ref) => {
    // Transcript / dictation
    const [input, setInput] = useState("");
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [interim, setInterim] = useState("");
    const [dictation, setDictation] = useState<DictationState>({
        active: false,
        pre: "",
        post: "",
        committed: "",
    });

    // Editor state
    const [aiReport, setAiReport] = useState("");
    const [editorHasContent, setEditorHasContent] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const hasAiContent = !!aiReport.trim();
    const [generating, setGenerating] = useState(false);
    const ctaLabel = generating
        ? hasAiContent ? "Updating…" : "Generating…"
        : hasAiContent ? "Update Report" : "Generate Report";
    const patientId = getQueryParam("studyId");

    const [viewMarkdownPreview, setViewMarkdownPreview] = useState(false);

    const md = useMemo(
        () =>
            new MarkdownIt({
                html: false,
                linkify: true,
                breaks: false,
                typographer: false,
            }),
        []
    );
    const editHtmlRef = useRef<string>("");

    // speech
    const { listening, start, stop, abort } = useSpeechRecognition({
        interimResults: true,
        onInterim: (t) => {
            setInterim(t);
            setDictation((d) => {
                if (!d.active) return d;
                const previewSegment = applyPunctuation(d.committed + t, { finalize: false });
                const newVal = d.pre + previewSegment + d.post;
                setInput(newVal);
                requestAnimationFrame(() => {
                    const el = textareaRef.current;
                    if (el) {
                        const pos = (d.pre + previewSegment).length;
                        el.selectionStart = el.selectionEnd = pos;
                    }
                });
                return d;
            });
        },
        onFinal: (final) => {
            setInterim("");
            setDictation((d) => {
                if (!d.active) return d;
                const committed = applyPunctuation(d.committed + final, { finalize: true });
                const newVal = d.pre + committed + d.post;
                setInput(newVal);
                requestAnimationFrame(() => {
                    const el = textareaRef.current;
                    if (el) {
                        const pos = (d.pre + committed).length;
                        el.selectionStart = el.selectionEnd = pos;
                    }
                });
                return { ...d, committed };
            });
        },
    });

    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            Placeholder.configure({ placeholder: "Your generated report will appear here. You can edit freely…" }),
            Table.configure({ resizable: true }),
            TableRow,
            TableHeader,
            TableCell,
        ],
        content: "",
        editorProps: {
            attributes: {
                class: `tiptap ${PROSE_COMPACT} focus:outline-none p-6 xl:p-8 leading-7 xl:leading-8`,
            },
        },
        onCreate: ({ editor }) =>
            setEditorHasContent(editor.getText().trim().length > 0),
        onUpdate: ({ editor }) => {
            setEditorHasContent(editor.getText().trim().length > 0);
            editHtmlRef.current = editor.getHTML();
        },
    });

    // Sync TipTap with current source (edited html or latest aiReport) when switching modes
    useEffect(() => {
        if (!editor || viewMarkdownPreview) return;

        const html =
            (editHtmlRef.current && editHtmlRef.current.trim())
                ? editHtmlRef.current
                : aiReport.trim()
                    ? (looksLikeHTML(aiReport.trim()) ? aiReport : md.render(normalizeMarkdown(aiReport)))
                    : "";

        if (!html) {
            editor.commands.clearContent();
            setEditorHasContent(false);
            return;
        }

        editor.commands.setContent(html, { emitUpdate: false });
        setEditorHasContent(editor.getText().trim().length > 0);
    }, [aiReport, editor, viewMarkdownPreview, md]);

    useEffect(() => {
        if (!editor) return;
        if (!viewMarkdownPreview && (editHtmlRef.current || aiReport.trim()))
            setEditorHasContent(true);
    }, [viewMarkdownPreview, editor, aiReport]);

    // pause dictation on generate
    useEffect(() => {
        if (generating && (listening || dictation.active)) {
            try { stop(); } catch { }
            setDictation({ active: false, pre: "", post: "", committed: "" });
            setInterim("");
        }
    }, [generating, listening, stop, dictation.active]);

    const canStartDictation = !generating;
    const isListening = listening || dictation.active;

    const startDictation = () => {
        if (generating) {
            toast.info("Report is being generated. Please wait until it finishes.");
            return;
        }
        const el = textareaRef.current;
        const val = input;
        const startIdx = el?.selectionStart ?? val.length;
        const endIdx = el?.selectionEnd ?? val.length;
        setDictation({ active: true, pre: val.slice(0, startIdx), post: val.slice(endIdx), committed: "" });
        setInterim("");
        start();
    };

    const stopAndCommit = () => {
        setDictation((d) => {
            if (d.active) {
                const newCommitted = interim ? applyPunctuation(d.committed + interim, { finalize: true }) : d.committed;
                const newVal = d.pre + newCommitted + d.post;
                setInput(newVal);
                requestAnimationFrame(() => {
                    const el = textareaRef.current;
                    if (el) {
                        const pos = (d.pre + newCommitted).length;
                        el.selectionStart = el.selectionEnd = pos;
                    }
                });
            }
            stop();
            return { active: false, pre: "", post: "", committed: "" };
        });
        setInterim("");
    };

    const abortAndDiscard = () => {
        setDictation((d) => {
            if (d.active) {
                const newVal = d.pre + d.post;
                setInput(newVal);
                requestAnimationFrame(() => {
                    const el = textareaRef.current;
                    if (el) el.selectionStart = el.selectionEnd = d.pre.length;
                });
            }
            return { active: false, pre: "", post: "", committed: "" };
        });
        setInterim("");
        abort();
    };

    const handleClear = () => {
        if (isListening) {
            try { abort(); } catch { }
            setDictation({ active: false, pre: "", post: "", committed: "" });
            setInterim("");
        }
        setInput("");
        requestAnimationFrame(() => textareaRef.current?.focus());
    };

    const pickAIText = (data: PatientReportResponse) => {
        if (typeof data === "string") return data;
        const candidates = [
            data?.result,
            data?.report_markdown,
            data?.report_html,
            data?.report_content,
            data?.html,
            data?.report,
            data?.text,
            data?.content,
            data?.markdown,
        ];
        for (const c of candidates) if (typeof c === "string" && c.trim()) return c;
        return "";
    };

    /* ---------- PRINT: clone just the report area and keep current CSS ---------- */
    const printWithin = (rootId: string) => {
        const root = document.getElementById(rootId);
        if (!root) return toast.error("Nothing to print.");
        const iframe = document.createElement("iframe");
        Object.assign(iframe.style, {
            position: "fixed",
            right: "0",
            bottom: "0",
            width: "0",
            height: "0",
            border: "0",
        } as CSSStyleDeclaration);
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow!.document;
        doc.open();

        const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
            .map((l) => (l as HTMLLinkElement).outerHTML)
            .join("");
        const inlineStyles = Array.from(document.querySelectorAll("style"))
            .map((s) => s.outerHTML)
            .join("");

        const FOOTER_H_MM = 22;
        doc.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8"/>
${links}${inlineStyles}
<style>
  @page { size: A4; margin: 16mm; }
  html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { margin: 0; padding-bottom: ${FOOTER_H_MM + 6}mm; }
  .print-root { min-height: 100vh; display: flex; flex-direction: column; }
  .print-body { flex: 1 1 auto; }
  footer { position: fixed; bottom: 0; left: 0; right: 0; height: ${FOOTER_H_MM}mm; }
</style>
</head>
<body>
  <div class="print-root">
    <div class="print-body">${root.innerHTML}</div>
  </div>
</body>
</html>`);
        doc.close();

        const cleanup = () => requestAnimationFrame(() => iframe.remove());
        iframe.contentWindow?.addEventListener("afterprint", cleanup);
        setTimeout(() => iframe.contentWindow?.print(), 50);
        setTimeout(cleanup, 3000);
    };

    const handleGenerate = async () => {
        const prompt = input.trim();
        if (generating) return;
        if (!prompt) return toast.error("Please enter a prompt.");

        if (isListening) {
            try { stop(); } catch { }
            setDictation({ active: false, pre: "", post: "", committed: "" });
            setInterim("");
        }

        setGenerating(true);
        try {
            const currentReport =
                editHtmlRef.current?.trim() ||
                (aiReport?.trim()
                    ? (looksLikeHTML(aiReport) ? aiReport : md.render(normalizeMarkdown(aiReport)))
                    : "");

            const payload = {
                text: prompt,
                patient_id: patientId,
                existing_report: currentReport,
            };

            const data: PatientReportResponse = await ApiServices.generatePatientReport(payload);
            const raw = String(pickAIText(data) || "").trim();

            // Normalize then render to HTML for TipTap
            const normalized = looksLikeHTML(raw) ? raw : normalizeMarkdown(raw);
            const html = looksLikeHTML(normalized) ? normalized : md.render(normalized);

            editHtmlRef.current = html;
            setAiReport(raw);

            // After generating, automatically switch to preview
            setViewMarkdownPreview(true);

            if (editor) editor.commands.setContent(html, { emitUpdate: true });
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.message || "Failed to generate report.";
            toast.error(msg);
        } finally {
            setGenerating(false);
        }
    };

    const isGenerateDisabled = generating || !input.trim();

    // Save report function
    const handleSaveReport = async () => {
        const printRoot = document.getElementById("report-print-root");
        if (!printRoot) {
            toast.error("No report content to save.");
            return;
        }

        window.dispatchEvent(new CustomEvent("stopVoiceDictation", {
            detail: { reason: "saving_dictation_report" }
        }));

        setIsSaving(true);
        try {
            await saveReport(printRoot, {
                studyId: patientId,
                successMessage: "Dictation report saved successfully!",
                errorMessage: "Failed to save dictation report",
            });
        } catch (err) {
            console.error("Error saving dictation report:", err);
        } finally {
            setIsSaving(false);
        }
    };

    // Hotkeys
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "enter") {
                e.preventDefault();
                if (!isGenerateDisabled) handleGenerate();
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
                e.preventDefault();
                printWithin("report-print-root");
            }
            if (e.key === "Escape" && (listening || dictation.active)) abortAndDiscard();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isGenerateDisabled, listening, dictation.active]);

    // Expose save method via ref
    useImperativeHandle(ref, () => ({
        handleSaveReport
    }));

    const hasReportContent = viewMarkdownPreview
        ? !!(editHtmlRef.current || aiReport.trim())
        : editorHasContent;

    const renderBrandFooter = hasReportContent;

    return (
        <div className="font-sans antialiased text-[15px] md:text-[16px] xl:text-[17px] 2xl:text-[18px] text-gray-900 dark:text-neutral-100">
            <main className="mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-7 xl:gap-9 items-stretch">
                {/* LEFT — Dictation */}
                <aside className="lg:col-span-5 min-h-0 flex flex-col">
                    <div className="ai-report-panel rounded-2xl border border-slate-200/70 dark:border-white/10 bg-white/90 dark:bg-neutral-900/70 shadow-sm backdrop-blur flex-1 min-h-0 flex flex-col">
                        <div className="px-5 py-5 xl:px-6 flex-1 min-h-0 flex flex-col">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-base xl:text-lg font-semibold tracking-tight">Your Transcript / Prompt</h3>
                            </div>

                            <div className="relative flex-1 min-h-0">
                                <textarea
                                    ref={textareaRef}
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder='Speak or type. Example: "Change liver size to 18 cm… create a table for canal diameters…"'
                                    className={cx(
                                        "ai-input-bg w-full h-full min-h-0 rounded-xl transition placeholder:text-slate-400 dark:placeholder:text-slate-500",
                                        "bg-white dark:bg-neutral-900",
                                        "border border-slate-200 dark:border-white/10",
                                        "px-4 py-4 pr-20 outline-none resize-none text-base xl:text-lg",
                                        "focus:ring-2 focus:ring-sky-500/30 focus:border-sky-400",
                                        "with-scrollbars"
                                    )}
                                />
                                <button
                                    type="button"
                                    onClick={handleClear}
                                    disabled={!input.trim()}
                                    title="Clear"
                                    aria-label="Clear"
                                    className={cx(
                                        "absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition",
                                        "backdrop-blur supports-[backdrop-filter]:bg-white/40 shadow ring-1 ring-black/10 dark:ring-white/10",
                                        "bg-white/90 text-gray-700 hover:bg-white dark:bg-neutral-900/80 dark:text-gray-200 dark:hover:bg-neutral-800",
                                        !input.trim() && "opacity-0 pointer-events-none"
                                    )}
                                >
                                    <X className="h-4 w-4" />
                                    <span className="hidden sm:inline">Clear</span>
                                </button>
                            </div>

                            <div className="mt-5 mb-2 flex items-center justify-center">
                                <button
                                    type="button"
                                    onClick={() => (isListening ? stopAndCommit() : startDictation())}
                                    className={cx(
                                        "mic-button mic-default shadow-md",
                                        isListening && "mic-listening",
                                        canStartDictation ? "mic-enabled" : "mic-disabled"
                                    )}
                                    aria-pressed={isListening}
                                    aria-label={isListening ? "Stop dictation" : "Start dictation"}
                                    title={isListening ? "Stop dictation" : "Start dictation"}
                                >
                                    {isListening ? (<Mic className="h-5 w-5" />) : (<MicOff className="h-5 w-5" />)}
                                </button>
                            </div>
                        </div>

                        <div className="ai-toolbar-bg relative bottom-0 left-0 right-0 -mx-5 px-5 py-4 bg-white/95 dark:bg-neutral-900/90 backdrop-blur rounded-b-2xl border-t border-slate-200/70 dark:border-white/10 z-20">
                            <button
                                onClick={handleGenerate}
                                disabled={isGenerateDisabled}
                                aria-busy={generating}
                                className={cx(
                                    "w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl font-semibold tracking-tight transition",
                                    "shadow-lg hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40",
                                    // Generating (Active Loading)
                                    generating
                                        ? "bg-slate-900/90 text-white cursor-wait dark:bg-blue-700 dark:text-white"
                                        : // Disabled (Empty)
                                        !input.trim()
                                            ? "bg-slate-100 text-slate-400 dark:bg-neutral-800 dark:text-neutral-500 cursor-not-allowed"
                                            : // Enabled (Running)
                                            "bg-slate-900 text-white hover:bg-slate-800 dark:bg-blue-600 dark:text-white dark:hover:bg-blue-500"
                                )}
                                title={
                                    isGenerateDisabled
                                        ? !input.trim()
                                            ? "Enter a prompt"
                                            : "Generate"
                                        : hasAiContent
                                            ? "Update Report"
                                            : "Generate Report"
                                }
                            >
                                {generating ? (<Loader2 className="h-5 w-5 animate-spin" />) : (<Wand2 className="h-5 w-5" />)}
                                <span>{ctaLabel}</span>
                            </button>
                        </div>
                    </div>
                </aside>

                {/* RIGHT — Editor / Preview */}
                <section className="lg:col-span-7 h-full min-h-0">
                    <div
                        className={cx(
                            "ai-report-panel rounded-2xl border border-slate-200/70 dark:border-white/10 bg-white/90 dark:bg-neutral-900/70 shadow-sm backdrop-blur",
                            "h-full min-h-0 relative overflow-hidden flex flex-col"
                        )}
                        style={{ maxHeight: "calc(100vh - 297px)", minHeight: "calc(100vh - 297px)" }}
                    >
                        {/* Toolbar / Editor header */}
                        <div className="ai-toolbar-bg sticky top-0 z-10 flex items-center gap-1.5 px-4 xl:px-6 py-3 border-b border-slate-200/70 dark:border-white/10 bg-white/95 dark:bg-neutral-900/80 rounded-t-2xl overflow-x-auto">
                            {/* <ToolbarButton onClick={() => editor?.chain().focus().toggleBold().run()} active={editor?.isActive("bold")} title="Bold">
                                <Bold className="h-5 w-5 xl:h-6 xl:w-6" />
                            </ToolbarButton>
                            <ToolbarButton onClick={() => editor?.chain().focus().toggleItalic().run()} active={editor?.isActive("italic")} title="Italic">
                                <Italic className="h-5 w-5 xl:h-6 xl:w-6" />
                            </ToolbarButton>
                            <ToolbarButton onClick={() => editor?.chain().focus().toggleUnderline().run()} active={editor?.isActive("underline")} title="Underline">
                                <UnderlineIcon className="h-5 w-5 xl:h-6 xl:w-6" />
                            </ToolbarButton> */}
                            {/* <div className="w-px h-7 bg-slate-200 dark:bg-white/10 mx-2" /> */}
                            <ToolbarButton onClick={() => editor?.chain().focus().toggleBulletList().run()} active={editor?.isActive("bulletList")} title="Bullet list">
                                <List className="h-5 w-5 xl:h-6 xl:w-6" />
                            </ToolbarButton>
                            <ToolbarButton onClick={() => editor?.chain().focus().toggleOrderedList().run()} active={editor?.isActive("orderedList")} title="Numbered list">
                                <ListOrdered className="h-5 w-5 xl:h-6 xl:w-6" />
                            </ToolbarButton>
                            <div className="w-px h-7 bg-slate-200 dark:bg-white/10 mx-2" />
                            <ToolbarButton onClick={() => editor?.chain().focus().setTextAlign("left").run()} title="Align left">
                                <AlignLeft className="h-5 w-5 xl:h-6 xl:w-6" />
                            </ToolbarButton>
                            <ToolbarButton onClick={() => editor?.chain().focus().setTextAlign("center").run()} title="Align center">
                                <AlignCenter className="h-5 w-5 xl:h-6 xl:w-6" />
                            </ToolbarButton>
                            <ToolbarButton onClick={() => editor?.chain().focus().setTextAlign("right").run()} title="Align right">
                                <AlignRight className="h-5 w-5 xl:h-6 xl:w-6" />
                            </ToolbarButton>

                            <div className="w-px h-7 bg-slate-200 dark:bg-white/10 mx-2" />
                            {/* <ToolbarButton
                                onClick={() => printWithin("report-print-root")}
                                title="Print report (only the report content + footer)"
                                disabled={!viewMarkdownPreview || (!editorHasContent && !aiReport.trim())}
                            >
                                <Printer className="h-5 w-5 xl:h-6 xl:w-6" />
                            </ToolbarButton> */}
                            <div className="w-px h-7 bg-slate-200 dark:bg-white/10 mx-2" />
                            <ToolbarButton
                                onClick={() => editor?.chain().focus().undo().run()}
                                title={viewMarkdownPreview ? "Undo (edit mode only)" : "Undo"}
                                disabled={viewMarkdownPreview}
                            >
                                <Undo className="h-5 w-5 xl:h-6 xl:w-6" />
                            </ToolbarButton>
                            <ToolbarButton
                                onClick={() => editor?.chain().focus().redo().run()}
                                title={viewMarkdownPreview ? "Redo (edit mode only)" : "Redo"}
                                disabled={viewMarkdownPreview}
                            >
                                <Redo className="h-5 w-5 xl:h-6 xl:w-6" />
                            </ToolbarButton>
                            <ToolbarButton
                                onClick={() => {
                                    if (!editorHasContent && !aiReport.trim()) {
                                        return toast.info("No content to edit. Generate a report first.");
                                    }
                                    setViewMarkdownPreview(false);
                                }}
                                title={editorHasContent || aiReport.trim() ? "Edit report" : "No content to edit"}
                                active={!viewMarkdownPreview}
                                disabled={!editorHasContent && !aiReport.trim()}
                            >
                                <Edit3 className="h-5 w-5 xl:h-6 xl:w-6" />
                            </ToolbarButton>

                            <ToolbarButton
                                onClick={handleSaveReport}
                                title="Save report"
                                active={viewMarkdownPreview}
                                disabled={!editorHasContent && !aiReport.trim()}
                            >
                                <Save className="h-5 w-5 xl:h-6 xl:w-6" />
                            </ToolbarButton>
                        </div>

                        {/* Editor body (print root wraps ONLY the report content + footer) */}
                        <div className={cx("flex-1 overflow-y-auto min-h-0 px-4 custom-scrollbar", hasReportContent ? "pb-8" : "pb-4")}>
                            <div id="report-print-root" className="flex flex-col">
                                {viewMarkdownPreview ? (
                                    <>
                                        <div className={`${PROSE_COMPACT} p-6 flex-1`}>
                                            {editHtmlRef.current?.trim()
                                                ? parse(editHtmlRef.current)
                                                : aiReport?.trim()
                                                    ? looksLikeHTML(aiReport)
                                                        ? parse(aiReport)
                                                        : parse(md.render(normalizeMarkdown(aiReport)))
                                                    : (
                                                        <div className="text-center text-slate-400 dark:text-slate-400">
                                                            <Wand2 className="mx-auto mb-3 h-8 w-8" />
                                                            <p className="font-medium text-[15px]">No content yet</p>
                                                            <p className="text-[15px] mt-1">Use the Transcript panel to generate your first report.</p>
                                                        </div>
                                                    )}
                                        </div>
                                        {/* {renderBrandFooter && <div className="mt-auto"><BrandFooter /></div>} */}
                                    </>
                                ) : (
                                    <>
                                        <div className="flex-1">
                                            {editor && <EditorContent editor={editor} />}
                                            {!editorHasContent && (
                                                <div className="pointer-events-none absolute inset-16 hidden lg:flex items-center justify-center">
                                                    <div className="text-center text-slate-400 dark:text-slate-500">
                                                        <Wand2 className="mx-auto mb-3 h-8 w-8" />
                                                        <p className="font-medium">No content yet</p>
                                                        <p className="text-sm">Use the Transcript panel to generate your first report.</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {/* {viewMarkdownPreview && (editorHasContent || !!aiReport.trim()) && <BrandFooter />} */}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
});

export default ReportCreateDictation;
